import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ItemList from '../../../Common/ItemList/ItemList'
import { Button, Grid, NoSsr } from '@mui/material'
import { AddRounded as AddRoundedIcon } from '@mui/icons-material'
import { type UserCreditCardStore } from '../../../UserData/_stores/UserCreditCardStore'
import CreditCard from './CreditCard'
import CreditCardSkeleton from './CreditCardSkeleton'
import AddCreditCardDialog from './AddCreditCardDialog'
import AccountHeader from '../../AccountHeader'
import AccountEmptyCard from '../../AccountEmptyCard'
import PaginationItemListStore from '../../../UserData/_stores/PaginationItemListStore'

const LIMIT = 6

export default function CreditCardList() {
  const [t] = useTranslation('account-v2')

  const [count, setCount] = useState(0)
  const [addDialogOpened, setAddDialogOpened] = useState(false)

  const loadingGrid = useMemo(() => {
    const listSkeletons = []

    for (let i = 0; i < LIMIT; i++) {
      listSkeletons.push(<CreditCardSkeleton key={i} />)
    }

    return listSkeletons
  }, [])

  const onAddOpen = useCallback(() => {
    setAddDialogOpened(true)
  }, [])

  const onAddClose = useCallback(() => {
    setAddDialogOpened(false)
  }, [])

  const renderCreditCard = useCallback((item: UserCreditCardStore) => {
    return <CreditCard creditCard={item} />
  }, [])

  const renderList = useCallback((items: JSX.Element | null, links: JSX.Element | null, loading: boolean) => {
    return <>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {loading
          ? loadingGrid
          : (items ?? <Grid item xs={12} md={6} lg={4}>
            <AccountEmptyCard
              title={t('No credit cards yet')}
              description={t('Save a card to make checkout faster next time.')}
              actionLabel={t('Add Credit card')}
              onAction={onAddOpen}
            />
          </Grid>)
        }
      </Grid>

      {links}
    </>
  }, [loadingGrid, onAddOpen, t])

  const onUpdate = useCallback((itemList: PaginationItemListStore|undefined) => {
    if (itemList) {
      setCount(itemList.get('total'))
    }
  }, [])

  return <div>
    <AccountHeader
      title={t('Credit cards')}
      actions={count > 0 ? <Button
        variant="contained"
        color="accent"
        startIcon={<AddRoundedIcon />}
        onClick={onAddOpen}
      >{t('Add Credit card')}</Button> : null}
    />
    <ItemList
      dataType="creditCards"
      item={renderCreditCard}
      limit={LIMIT}
      render={renderList}
      onUpdate={onUpdate}
    />

    <NoSsr>
      <AddCreditCardDialog
        opened={addDialogOpened}
        onClose={onAddClose}
      />
    </NoSsr>
  </div>
}
